import { formatProgressUnit, resolveResourceType } from '@/shared/resourceModuleParity'
import type { ObjectiveWithProgress } from '@/types/objective'

import type { LadderState } from './milestones'

/**
 * Textos do card de escada.
 *
 * PT direto, como o resto do módulo de objetivos — o app não tem i18n, só um
 * mapper de strings PT que este módulo não usa.
 *
 * Vocabulário travado: o degrau é **marco** e o agrupamento é **meta**. Nunca
 * "missão", "jornada", "nível" ou "etapa" — todos já nomeiam módulos visíveis
 * ao participante, e reusá-los aqui colidiria de frente.
 *
 * **"Desafio" saiu da lista por decisão do dev em 2026-09-13**, quando passou a
 * ser o nome da própria feature: continuar barrando a palavra proibiria a cópia
 * de escada de nomear a seção em que ela vive. A ambiguidade com o recurso
 * `Challenge`, que também rotula "Desafio" em condição de objetivo, foi aceita
 * na mesma decisão — o registro é o que mantém a volta atrás barata. Ver
 * `_features/2026/sprint-09/objectives-label-registry/spec/requirements.md`,
 * "Convivência com a entidade Challenge".
 *
 * `MilestoneObjectiveCard.test.tsx` impõe as quatro que continuam barradas.
 */

const plural = (count: number, singular: string, pluralForm: string) =>
  count === 1 ? singular : pluralForm

/** Nome do recurso na quantidade certa: "1 parceiro" / "7 parceiros". */
export function resourceNoun(
  milestone: ObjectiveWithProgress | null,
  count: number
): string {
  const condition = milestone?.objective_conditions?.[0]
  const resolved = resolveResourceType(condition?.resource_type)
  const label = count === 1 ? resolved.label.singular : resolved.label.plural
  return label.toLowerCase()
}

/** Linha de ação do card, por estado da escada. */
export function ladderActionLabel(state: LadderState, sample: ObjectiveWithProgress | null): string {
  if (state.completed) {
    return `${state.currentCount} ${resourceNoun(sample, state.currentCount)}`
  }

  if (state.currentCount === 0) {
    const target = state.nextTarget ?? 0
    return `Complete ${target} ${resourceNoun(sample, target)}`
  }

  return `${state.currentCount} ${resourceNoun(sample, state.currentCount)}`
}

/** Recompensa em destaque — sempre a do próximo marco, nunca soma nem média. */
export function ladderRewardLabel(state: LadderState): string {
  if (state.completed) {
    return `${state.totalCoinReward} conquistadas`
  }
  return `+${state.nextReward ?? 0} moedas`
}

/** Linha abaixo da barra — liga o que falta ao prêmio do próximo marco. */
export function ladderNextStepLabel(
  state: LadderState,
  sample: ObjectiveWithProgress | null
): string | null {
  if (state.completed) return null

  const reward = state.nextReward ?? 0
  const coins = `+${reward} moedas`

  if (state.currentCount === 0) {
    const target = state.nextTarget ?? 0
    return `Complete ${target} ${resourceNoun(sample, target)} para ${coins}`
  }

  if (state.remaining === null) return null

  return `Faltam ${state.remaining} ${resourceNoun(sample, state.remaining)} para ${coins}`
}

/** Linha de resumo abaixo da barra (sheet / home compacto). */
export function ladderSummaryLabel(
  state: LadderState,
  sample: ObjectiveWithProgress | null
): string {
  const total = state.totalMilestones

  if (state.completed) {
    return `${total} de ${total} ${plural(total, 'marco', 'marcos')}`
  }

  if (state.currentCount === 0) {
    return `${total} ${plural(total, 'marco', 'marcos')} de recompensa`
  }

  const next = state.nextTarget ?? 0
  return `${state.achievedCount} de ${total} ${plural(total, 'marco', 'marcos')} · próximo: ${next} ${resourceNoun(sample, next)}`
}

/** Contagem cumulativa na escada inteira — ex.: "10 de 75 check-ins". */
export function ladderMilestoneCountLabel(
  state: LadderState,
  sample: ObjectiveWithProgress | null
): string {
  const total = state.totalTarget
  const condition = sample?.objective_conditions?.[0]
  return `${state.currentCount} de ${total} ${formatProgressUnit(condition, total)}`
}

/**
 * Contagem do marco ativo — o mais próximo ainda não conquistado.
 *
 * É o texto da linha superior da barra: o participante precisa saber quanto
 * falta para o objetivo em foco, não para o fim da escada inteira.
 */
export function ladderActiveMilestoneLabel(
  state: LadderState,
  sample: ObjectiveWithProgress | null
): string {
  const activeMilestone = state.nextMilestone ?? sample
  const condition =
    activeMilestone?.objective_conditions?.[0] ?? sample?.objective_conditions?.[0]

  if (state.completed || state.nextTarget == null) {
    const total = state.totalTarget
    return `${state.currentCount} de ${total} ${formatProgressUnit(condition, total)}`
  }

  return `${Math.min(state.currentCount, state.nextTarget)} de ${state.nextTarget} ${formatProgressUnit(
    condition,
    state.nextTarget
  )}`
}

/** Percentual de avanço dentro do marco ativo (0–100). */
export function ladderActiveMilestonePercent(state: LadderState): number {
  if (state.completed || state.nextTarget == null || state.nextTarget <= 0) {
    return 100
  }

  return Math.min(100, Math.round((state.currentCount / state.nextTarget) * 100))
}

/** Texto anunciado por leitor de tela na barra de progresso. */
export function ladderAriaValueText(
  state: LadderState,
  sample: ObjectiveWithProgress | null
): string {
  const base = ladderActiveMilestoneLabel(state, sample)

  if (state.completed || state.nextReward == null || state.nextReward <= 0) {
    return base
  }

  return `${base}, próximo marco: +${state.nextReward} moedas`
}

/** Versão curta para mobile — `repete 16x`. */
export function ladderRepeatLabelShort(totalMilestones: number): string {
  return `repete ${totalMilestones}x`
}

/** Versão longa para telas maiores — `repete 16 vezes`. */
export function ladderRepeatLabel(totalMilestones: number): string {
  if (totalMilestones <= 1) return 'repete 1 vez'
  return `repete ${totalMilestones} vezes`
}

/** Rótulo do estado de conquista, exibido logo após cruzar um marco. */
export const MILESTONE_ACHIEVED_LABEL = 'Marco conquistado!'

/** Rótulo da meta concluída. */
export const LADDER_COMPLETED_LABEL = 'Meta concluída'
